import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import { colors, company, contact, images } from "../../config/site";

/**
 * Reusable branded "printable sheet" wrapper for resource downloads.
 * - On screen: shows site Header + Footer, brand colors, and a "Print / Save as PDF" button.
 * - On print: hides chrome, shows clean A4/Letter-friendly branded layout with logo header.
 *
 * Props:
 *   eyebrow:     small caps label above title (e.g. "RENTERS · RESOURCE")
 *   title:       sheet title
 *   subtitle:    optional one-line description below title
 *   backHref:    where the back link goes (default /resources/renters)
 *   backLabel:   label of back link
 *   children:    the body of the sheet
 */
const PrintableSheet = ({
  eyebrow = "DORSEY TAYLOR HOMES · RESOURCE",
  title,
  subtitle,
  backHref = "/resources/renters",
  backLabel = "Back to Renter Resources",
  children,
  testId,
}) => {
  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }} data-testid={testId}>
      {/* Hide header/footer/back/print on print */}
      <div className="print:hidden">
        <Header />
      </div>

      <main className="pt-24 pb-16 px-4 print:pt-0 print:pb-0 print:px-0">
        <div className="container mx-auto max-w-4xl print:max-w-none">
          {/* Back + Print row (hidden on print) */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 print:hidden">
            <Link
              to={backHref}
              className="inline-flex items-center text-sm font-semibold"
              style={{ color: colors.primary }}
              data-testid="printable-back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {backLabel}
            </Link>
            <Button
              onClick={handlePrint}
              className="rounded-full px-5 py-5 font-semibold text-white"
              style={{ backgroundColor: colors.terracotta }}
              data-testid="printable-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print / Save as PDF
            </Button>
          </div>

          {/* Sheet — the actual printable card */}
          <div
            className="rounded-3xl shadow-lg overflow-hidden print:shadow-none print:rounded-none"
            style={{ backgroundColor: colors.white }}
          >
            {/* Branded header strip */}
            <div
              className="px-8 py-6 flex items-center justify-between gap-4 border-b-4"
              style={{ backgroundColor: colors.primary, borderColor: colors.terracotta }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 bg-white p-1"
                  style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                >
                  <img
                    src={images.logo}
                    alt={`${company.name} logo`}
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
                <div className="text-white">
                  <p
                    className="text-xs uppercase tracking-widest font-bold"
                    style={{ color: colors.gold }}
                  >
                    {company.name}
                  </p>
                  <p className="text-sm opacity-90">{company.tagline}</p>
                </div>
              </div>
              <div className="hidden sm:block text-right text-white">
                <p className="text-xs uppercase tracking-wider" style={{ color: colors.gold }}>
                  Dorsey Taylor Homes
                </p>
                <p className="text-sm font-semibold">{contact.phone}</p>
              </div>
            </div>

            {/* Title */}
            <div className="px-8 py-8 print:py-6" style={{ backgroundColor: colors.ivory }}>
              <p
                className="text-xs uppercase tracking-widest font-bold mb-2"
                style={{ color: colors.terracotta }}
              >
                {eyebrow}
              </p>
              <h1
                className="text-3xl lg:text-4xl font-bold leading-tight"
                style={{ color: colors.charcoal }}
              >
                {title}
              </h1>
              {subtitle && (
                <p className="mt-3 text-base lg:text-lg" style={{ color: colors.textMuted }}>
                  {subtitle}
                </p>
              )}
            </div>

            {/* Body */}
            <div className="px-8 py-8 print:px-6 print:py-5">{children}</div>

            {/* Footer strip on the sheet itself */}
            <div
              className="px-8 py-5 text-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
              style={{ backgroundColor: colors.cream, color: colors.textMuted }}
            >
              <span>
                © {new Date().getFullYear()} {company.legalName} · {company.tagline}
              </span>
              <span>
                {contact.phone} · {contact.email} · {contact.address.line2}
              </span>
            </div>
          </div>
        </div>
      </main>

      <div className="print:hidden">
        <Footer />
      </div>
    </div>
  );
};

export default PrintableSheet;
